/** Hero banners: one per section, shared by that section's pages. Files live in public/images/hero. */
export type GameHeroKey =
  | 'game-warhammer-40k'
  | 'game-age-of-sigmar'
  | 'game-kill-team'
  | 'game-horus-heresy'
  | 'game-old-world'
  | 'game-blood-bowl'
  | 'game-middle-earth'
  | 'game-necromunda';
export type HeroKey = 'home' | 'hobby' | 'play' | 'grimm' | 'you' | 'events' | GameHeroKey;

/** Intrinsic size of the large files; the small ones are 800 px wide at the same ratio. */
export const HERO_SIZE = { width: 1584, height: 672 } as const;

const PREFIXES: Array<[HeroKey, string[]]> = [
  ['home', ['/home']],
  ['hobby', ['/hobby', '/collection', '/paint-recipes', '/journal', '/projects', '/community']],
  ['play', ['/play', '/battles', '/rosters', '/near-me', '/tools']],
  ['grimm', ['/grimm']],
  ['you', ['/you', '/help']],
  ['events', ['/events', '/venues']],
];

/** Pages that are forms or dense tools stay plain even inside a hero section. */
const PLAIN: string[] = ['/battles/log', '/battles/new', '/rosters/new', '/projects/new', '/you/settings', '/near-me/submit'];

/** The hero for a route, or null when the page should keep the plain header. */
export function heroKeyForPath(pathname: string): HeroKey | null {
  const on = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);
  if (PLAIN.some(on)) return null;
  for (const [key, prefixes] of PREFIXES) {
    if (prefixes.some(on)) return key;
  }
  return null;
}

const GAME_HEROES: Record<string, GameHeroKey> = {
  'warhammer-40k': 'game-warhammer-40k',
  'age-of-sigmar': 'game-age-of-sigmar',
  'kill-team': 'game-kill-team',
  'horus-heresy': 'game-horus-heresy',
  'old-world': 'game-old-world',
  'blood-bowl': 'game-blood-bowl',
  'blood-bowl-2025': 'game-blood-bowl',
  'middle-earth': 'game-middle-earth',
  necromunda: 'game-necromunda',
};

/** The scenery for a game system (canonical slug), or null so the section's archive scene is used. */
export const heroKeyForGame = (slug?: string | null): GameHeroKey | null => (slug ? (GAME_HEROES[slug] ?? null) : null);

export const heroSrc = (key: HeroKey, size: 'sm' | 'lg', format: 'avif' | 'webp'): string =>
  `/images/hero/${key}-${size}.${format}`;
